import { useRef, useEffect, useState, useCallback } from "react";
import { useLanguage } from "../context/LanguageContext";

function parseMetricValue(value) {
    if (value.includes("-")) return null;
    const cleaned = value.replace(/\./g, "").replace(/,/g, "");
    const match = cleaned.match(/^([+]?)(\d+)/);
    if (!match) return null;
    return {
        prefix: match[1],
        number: parseInt(match[2], 10),
        suffix: value.replace(/^[+]?[\d.,]+/, ""),
        original: value,
    };
}

function formatNumber(num, locale) {
    if (locale === "es") {
        return num.toLocaleString("es-ES");
    }
    return num.toLocaleString("en-US");
}

function AnimatedMetric({ value, label, locale, isVisible, index }) {
    const [displayValue, setDisplayValue] = useState(value);
    const parsed = parseMetricValue(value);
    const hasAnimated = useRef(false);

    useEffect(() => {
        if (!isVisible || !parsed || hasAnimated.current) return;
        hasAnimated.current = true;

        const duration = 2000;
        const startTime = performance.now();

        const animate = (timestamp) => {
            const elapsed = timestamp - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            const current = Math.floor(eased * parsed.number);
            setDisplayValue(`${parsed.prefix}${formatNumber(current, locale)}${parsed.suffix}`);
            if (progress < 1) {
                requestAnimationFrame(animate);
            }
        };

        const timer = setTimeout(() => {
            requestAnimationFrame(animate);
        }, index * 200);

        return () => clearTimeout(timer);
    }, [isVisible, parsed, locale, index, value]);

    return (
        <div
            className="text-center bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 md:p-8 hover:shadow-lg transition-all duration-300 overflow-hidden"
            style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? "translateY(0)" : "translateY(24px)",
                transition: `opacity 0.6s ease ${index * 0.15}s, transform 0.6s ease ${index * 0.15}s`,
            }}
        >
            <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-blue-600 dark:text-blue-400 tabular-nums whitespace-nowrap">
                {displayValue}
            </div>
            <p className="mt-3 text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                {label}
            </p>
        </div>
    );
}

export default function Testimonials() {
    const { t, lang } = useLanguage();
    const sectionRef = useRef(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) setIsVisible(true);
            },
            { threshold: 0.2 }
        );
        const current = sectionRef.current;
        if (current) observer.observe(current);
        return () => {
            if (current) observer.unobserve(current);
        };
    }, []);

    const metrics = [
        { value: t.testimonials.metrics.translations, label: t.testimonials.metrics.translationsLabel },
        { value: t.testimonials.metrics.clients, label: t.testimonials.metrics.clientsLabel },
        { value: t.testimonials.metrics.satisfaction, label: t.testimonials.metrics.satisfactionLabel },
        { value: t.testimonials.metrics.delivery, label: t.testimonials.metrics.deliveryLabel },
    ];

    return (
        <section className="py-20 md:py-32 px-6" ref={sectionRef} data-testid="testimonials-section">
            <div className="max-w-7xl mx-auto">
                <div
                    className="text-center mb-16"
                    style={{
                        opacity: isVisible ? 1 : 0,
                        transform: isVisible ? "translateY(0)" : "translateY(24px)",
                        transition: "opacity 0.6s ease, transform 0.6s ease",
                    }}
                >
                    <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">
                        {t.testimonials.title}
                    </h2>
                    <p className="mt-4 text-base md:text-lg text-slate-500 dark:text-slate-400">
                        {t.testimonials.subtitle}
                    </p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                    {metrics.map((metric, i) => (
                        <AnimatedMetric
                            key={i}
                            value={metric.value}
                            label={metric.label}
                            locale={lang}
                            isVisible={isVisible}
                            index={i}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
}
